import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Tipos ──────────────────────────────────────────────────────────────────────

export interface GeoSummaryRow {
  label:          string;
  city:           string;
  state:          string;
  contact_count:  number;
  leader_count:   number;
  avg_engagement: number;
  center_lat:     number | null;
  center_lng:     number | null;
}

export interface ContactMapPoint {
  id:               string;
  full_name:        string;
  category:         string;
  engagement_score: number;
  neighborhood:     string | null;
  city:             string | null;
  latitude:         number;
  longitude:        number;
}

export interface ContactMapDetail {
  id:               string;
  full_name:        string;
  phone:            string | null;
  email:            string | null;
  category:         string;
  tags:             string[];
  engagement_score: number;
  neighborhood:     string | null;
  city:             string | null;
  state_code:       string | null;
  notes:            string | null;
  latitude:         number | null;
  longitude:        number | null;
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

/** Agrega contatos por bairro ou cidade para o painel lateral e heatmap */
export function useGeoSummary(groupBy: 'neighborhood' | 'city' = 'neighborhood') {
  return useQuery({
    queryKey: ['geo-summary', groupBy],
    queryFn: async (): Promise<GeoSummaryRow[]> => {
      const { data, error } = await (supabase as any)
        .rpc('contact_geo_summary', { p_group_by: groupBy });
      if (error) throw error;
      return (data ?? []) as GeoSummaryRow[];
    },
    staleTime: 60_000,
  });
}

/** Contatos com coordenadas para os marcadores no mapa */
export function useContactMapPoints(filters?: {
  neighborhood?: string;
  city?:         string;
  category?:     string;
}) {
  return useQuery({
    queryKey: ['contact-map-points', filters],
    queryFn: async (): Promise<ContactMapPoint[]> => {
      let query = (supabase as any)
        .from('contacts')
        .select('id, full_name, category, engagement_score, neighborhood, city, latitude, longitude')
        .eq('active', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (filters?.neighborhood) query = query.ilike('neighborhood', `%${filters.neighborhood}%`);
      if (filters?.city)         query = query.ilike('city',         `%${filters.city}%`);
      if (filters?.category)     query = query.eq('category',        filters.category);

      const { data, error } = await query.limit(2000);
      if (error) throw error;
      return (data ?? []) as ContactMapPoint[];
    },
    staleTime: 30_000,
  });
}

/** Detalhe completo de um contato pelo id (usado no painel de pino) */
export function useContactMapDetail(id: string | null) {
  return useQuery({
    queryKey: ['contact-map-detail', id],
    enabled:  !!id,
    queryFn: async (): Promise<ContactMapDetail | null> => {
      const { data, error } = await (supabase as any)
        .from('contacts')
        .select('id, full_name, phone, email, category, tags, engagement_score, neighborhood, city, state_code, notes, latitude, longitude')
        .eq('id', id)
        .single();
      if (error) return null;
      return data as ContactMapDetail;
    },
    staleTime: 30_000,
  });
}

/** Stats gerais para o header do Field Intelligence */
export function useGeoStats() {
  return useQuery({
    queryKey: ['geo-stats'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('contacts')
        .select('category, latitude, neighborhood, city')
        .eq('active', true);
      if (error) throw error;

      const rows = (data ?? []) as Array<{
        category: string;
        latitude: number | null;
        neighborhood: string | null;
        city: string | null;
      }>;

      const total          = rows.length;
      const withCoords     = rows.filter((r) => r.latitude != null).length;
      const uniqueNeighborhoods = new Set(rows.filter((r) => r.neighborhood).map((r) => r.neighborhood)).size;
      const uniqueCities        = new Set(rows.filter((r) => r.city).map((r) => r.city)).size;
      const leaders             = rows.filter((r) => r.category === 'lideranca').length;

      return { total, withCoords, uniqueNeighborhoods, uniqueCities, leaders };
    },
    staleTime: 60_000,
  });
}

// ── Exportação CSV ─────────────────────────────────────────────────────────────

/** Baixa CSV com os contatos da área/categoria selecionados */
export async function exportAreaContacts(filters: {
  neighborhood?: string;
  city?:         string;
  category?:     string;
  areaLabel?:    string;
}): Promise<void> {
  let query = (supabase as any)
    .from('contacts')
    .select('full_name, phone, email, category, neighborhood, city, state_code, engagement_score, tags, notes')
    .eq('active', true);

  if (filters.neighborhood) query = query.ilike('neighborhood', `%${filters.neighborhood}%`);
  if (filters.city)         query = query.ilike('city',         `%${filters.city}%`);
  if (filters.category)     query = query.eq('category',        filters.category);

  const { data, error } = await query.limit(10_000);
  if (error) throw error;

  const rows = (data ?? []) as Array<Record<string, unknown>>;
  if (!rows.length) return;

  const headers = ['Nome', 'Telefone', 'E-mail', 'Categoria', 'Bairro', 'Cidade', 'UF', 'Engajamento', 'Tags', 'Obs.'];
  const csvRows = [
    headers.join(';'),
    ...rows.map((r) =>
      [
        r.full_name,
        r.phone ?? '',
        r.email ?? '',
        r.category,
        r.neighborhood ?? '',
        r.city ?? '',
        r.state_code ?? '',
        r.engagement_score,
        Array.isArray(r.tags) ? r.tags.join('|') : '',
        (r.notes ?? '').toString().replace(/[\r\n;]+/g, ' '),
      ]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(';'),
    ),
  ];

  const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `campo_${filters.areaLabel ?? 'todos'}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
